package info.rezident.app.ui.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import info.rezident.app.ui.theme.AppTheme
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

/**
 * Professional gradient header for the recommendations page like in MedPath design
 *
 * @param title Main heading text
 * @param description Descriptive text below the title
 * @param subtitle Optional small text above the title, hidden when null
 * @param modifier Additional modifiers to apply
 */
@Composable
fun GradientHeader(
    title: String,
    description: String,
    subtitle: String? = null,
    isUnlocked: Boolean = false,
    modifier: Modifier = Modifier
) {
    val breakpoint = rememberBreakpoint()
    val isDark = ColorMode.current.isDark

    Box(
        modifier = modifier
            .fillMaxWidth()
            .run {
                when {
                    isUnlocked -> backgroundColor(Color("#22c55e")) // Green color from progress bar
                    isDark -> backgroundColor(AppTheme.colors.onAccent)
                    else -> styleModifier {
                        // Gradient from skyBlue to blueGreen using your brand colors
                        property("background", "linear-gradient(135deg, #8ECAE6 0%, #219EBC 100%)")
                    }
                }
            }
            .padding(
                left = if (breakpoint < Breakpoint.MD) 16.px else 32.px,
                top = if (breakpoint < Breakpoint.MD) 32.px else 48.px,
                right = if (breakpoint < Breakpoint.MD) 16.px else 32.px,
                bottom = if (breakpoint < Breakpoint.MD) 32.px else 48.px
            ),
        contentAlignment = Alignment.Center
    ) {
        Column(
            Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            if (subtitle != null) {
                SpanText(
                    text = subtitle,
                    modifier = AppTheme.typography.body1Medium.toModifier()
                        .color(
                            if (isUnlocked) {
                                AppTheme.colors.onAccent
                            } else {
                                AppTheme.colors.text
                            }
                        )
                        .textAlign(TextAlign.Center)
                        .margin(bottom = 8.px)
                        .fontWeight(FontWeight.SemiBold)
                )
            }

            SpanText(
                text = title,
                modifier = AppTheme.typography.h1.toModifier()
                    .color(
                        if (isUnlocked) {
                            AppTheme.colors.onAccent
                        } else {
                            AppTheme.colors.text
                        }
                    )
                    .textAlign(TextAlign.Center)
                    .margin(bottom = 12.px)
            )

            SpanText(
                text = description,
                modifier = AppTheme.typography.body1.toModifier()
                    .color(
                        if (isUnlocked) {
                            AppTheme.colors.onAccent
                        } else {
                            AppTheme.colors.text
                        }
                    )
                    .textAlign(TextAlign.Center)
                    .lineHeight(1.5)
                    .fillMaxWidth(if (breakpoint < Breakpoint.MD) 85.percent else 65.percent)
            )
        }
    }
}
